import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { WhoAmI } from '../../model/user/whoAmI.model';
import { AuthorityUtil } from '../../util/authority.util';

type ZendeskMessengerCommand = 'show' | 'hide' | 'open' | 'close' | 'logoutUser';
type ZendeskApi = (...args: unknown[]) => void;

@Injectable({
    providedIn: 'root',
})
export class ZendeskWidgetService {
    private readonly _document: Document = inject(DOCUMENT);
    private readonly _platformId: object = inject(PLATFORM_ID);
    private readonly _transloco: TranslocoService = inject(TranslocoService);
    private _initialized: boolean = false;

    constructor() {
        this._transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe((lang) => {
            this._setMessengerLocale(lang);
        });
    }

    initialize(): void {
        if (!isPlatformBrowser(this._platformId)) {
            return;
        }

        if (this._document.getElementById('ze-snippet')) {
            this._initialized = true;
            this._showMessenger();
            return;
        }

        if (this._initialized) {
            this._showMessenger();
            return;
        }

        const script = this._document.createElement('script');
        script.id = 'ze-snippet';
        script.src = 'https://static.zdassets.com/ekr/snippet.js?key=68b27f95-d728-4fb6-a840-789fe5073e13';
        script.async = true;
        script.defer = true;
        script.onload = () => this._showMessenger();

        this._document.body.appendChild(script);
        this._initialized = true;
        this._showMessenger();
    }

    initializeForUser(user: WhoAmI): void {
        if (user.hasAuthority(AuthorityUtil.ROLE_MSS_VIEWER)) {
            this.initialize();
            return;
        }

        this.close();
    }

    close(): void {
        if (!isPlatformBrowser(this._platformId)) {
            return;
        }

        this._invokeMessenger('close');
        this._invokeMessenger('logoutUser');
        this._invokeMessenger('hide');
    }

    private _showMessenger(): void {
        this._setMessengerLocale();
        this._invokeMessenger('show');
    }

    private _setMessengerLocale(lang: string = this._transloco.getActiveLang()): void {
        this._invokeZendesk('messenger:set', 'locale', lang);
    }

    private _invokeMessenger(command: ZendeskMessengerCommand): void {
        this._invokeZendesk('messenger', command);
    }

    private _invokeZendesk(...args: unknown[]): void {
        if (!isPlatformBrowser(this._platformId)) {
            return;
        }

        const zE = (this._document.defaultView as (Window & { zE?: ZendeskApi }) | null)?.zE;

        if (typeof zE !== 'function') {
            return;
        }

        zE(...args);
    }
}
